import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import { useHistory } from 'react-router';
import themePrimary from '../../styles/themePrimary';
import { makeStyles } from '@material-ui/core';
import { GetReceiptResponse } from '../../models/postcheckout/GetReceiptResponse';
import EmailReceipt from './EmailReceipt';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutState, CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { String_System_GeneralError, String_System_ConnectionError } from '../../utilities/StringHelper';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { getUrlParameter } from '../../utilities/UrlHelper';
import Loader from '../common/Loader';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { Receipt } from '../../models/postcheckout/Receipt';

const useStyles = makeStyles({
    headerContainer: {
        backgroundColor: themePrimary.palette.secondary.main,
        paddingLeft: ElementDimensions.padding10 + 'px',
        paddingRight: ElementDimensions.padding10 + 'px',
        float: 'left',
        width: '100%',
    },
    headerTextLeft: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
        float: 'left',
    },
    headerTextRight: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
        float: 'right',
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    root: {
        height: '100%',
        overflow: 'hidden',
    },
    rootLandscape: {
        marginTop: '0',
    },
    titleContainer: {
        height: ElementDimensions.headerBarHeight + 'px',
        display: 'flex',
        backgroundColor: themePrimary.palette.secondary.main,
        textAlign: 'center',
    },
    titleInnerContainer: {
        gridTemplateColumns: 'calc(35vw - ' + ElementDimensions.padding10 + 'px) calc(60vw - ' + ElementDimensions.padding10 + 'px)',
        display: 'grid',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    titleTextLeft: {
        textAlign: 'left',
        fontSize: '1.4em',
    },
    titleTextRight: {
        textAlign: 'right',
        fontSize: '1.4em',
    },
});

const EmailViewReceipt = (props: any) => {
    let history = useHistory();
    const [receipt, setReceipt] = React.useState<Receipt | null>(null);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [loadError, setLoadError] = React.useState<boolean>(false);    
    const [userId, setUserId] = React.useState<number | null>(null);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    let loadRetries: number = 0;

    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();

        if (!loaded) {
            loadData(source);
        }
        
        return () => {
            source.cancel();
        };
    }, []);

    const loadData = async (cancelTokenSource: CancelTokenSource) => {
        var paymentId = parseInt(getUrlParameter(history.location.search, 'p'))

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/receipt/" + paymentId!!

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.get(url, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as GetReceiptResponse;

            const state: CheckOutState = checkOutState.state;
            state.restaurantId = r.restaurant_id;
            state.restaurantName = r.restaurant_name;
            state.restaurantLogoUrl = process.env.REACT_APP_IMAGE_URL + '/' + r.restaurant_id.toString() + '.png';

            if (r.skin.use_custom_skin) {
                state.skin = r.skin;
            }

            checkOutState.setState(state);
            setUserId(r.user_id);
            
            if (r.success === true) {
                setReceipt(r.receipt);
                setLoaded(true);
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
                setLoadError(true);
                setLoaded(true);
            }
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogOpen(true);
            setLoadError(true);
        });
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    return (
        <div className={classes.root}>
            {!loaded && !loadError &&
                <div className={classes.loadingContainer}>
                    {!loaded &&
                        <Loader />                    
                    }
                </div>
            }

            {loaded && !loadError &&
                <div>
                    <div>
                        {receipt !== null && receipt !== undefined &&
                            <EmailReceipt receipt={receipt} />
                        }
                    </div>
                </div>
            }

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

        </div>
    );
};

export default EmailViewReceipt;